import type { GridFilterModel } from '@mui/x-data-grid-premium';
import type { ServerGridFilter } from './gridFilterTypes';

// `1e999` parses to Infinity, and MUI's own numeric operators only guard NaN.
function isNonFiniteNumber(value: unknown): boolean {
  return typeof value === 'number' && !Number.isFinite(value);
}

function hasNonFiniteValue(value: unknown): boolean {
  return Array.isArray(value) ? value.some(isNonFiniteNumber) : isNonFiniteNumber(value);
}

// `JSON.stringify` writes every non-finite number as `null`, so a `null` coming back out of
// localStorage is a round-tripped Infinity/NaN.
// `equals` and `not` are the ONLY PrismaFilter operators typed to accept a real `null`
// (gridFilterTypes.ts); `not: null` is the one where the null IS the constraint
// ("is not blank").
const NULLABLE_OPERATORS = new Set(['not']);

function isPersistedNonFinite(value: unknown): boolean {
  return value === null;
}

function hasPersistedNonFiniteValue(value: unknown): boolean {
  return Array.isArray(value)
    ? value.some((entry) => isPersistedNonFinite(entry) || isNonFiniteNumber(entry))
    : isPersistedNonFinite(value) || isNonFiniteNumber(value);
}

function dropFilterItems(
  filterModel: GridFilterModel | undefined,
  isPoisoned: (value: unknown) => boolean
): GridFilterModel | undefined {
  if (!filterModel?.items) {
    return filterModel;
  }

  const keptItems = filterModel.items.filter((item) => !isPoisoned(item.value));

  return keptItems.length === filterModel.items.length ? filterModel : { ...filterModel, items: keptItems };
}

function dropFilterConditions(
  filters: ServerGridFilter[] | undefined,
  isPoisoned: (value: unknown) => boolean
): ServerGridFilter[] | undefined {
  if (!filters) {
    return filters;
  }

  return filters.map((filter) => ({
    ...filter,
    where: (filter.where ?? []).filter(
      (condition) =>
        !Object.entries(condition).some(
          ([key, value]) => key !== 'mode' && !(value === null && NULLABLE_OPERATORS.has(key)) && isPoisoned(value)
        )
    ),
  }));
}

/** Drops MUI filter items carrying a non-finite number, so they never reach persisted column state. */
export function dropNonFiniteFilterItems(filterModel: GridFilterModel | undefined): GridFilterModel | undefined {
  return dropFilterItems(filterModel, hasNonFiniteValue);
}

/** The read-side twin of `dropNonFiniteFilterItems`, for column state that was persisted without it. */
export function dropPersistedNonFiniteFilterItems(
  filterModel: GridFilterModel | undefined
): GridFilterModel | undefined {
  return dropFilterItems(filterModel, hasPersistedNonFiniteValue);
}

/**
 * Drops server-filter conditions carrying a non-finite number, leaving the filter entry
 * itself in place — a chip the user is still filling in has an empty `where` too.
 * Compose with `stripEmptyFilters` wherever an entry with nothing left to constrain is junk.
 */
export function dropNonFiniteFilterConditions(filters: ServerGridFilter[]): ServerGridFilter[];
export function dropNonFiniteFilterConditions(filters: ServerGridFilter[] | undefined): ServerGridFilter[] | undefined;
export function dropNonFiniteFilterConditions(filters: ServerGridFilter[] | undefined): ServerGridFilter[] | undefined {
  return dropFilterConditions(filters, hasNonFiniteValue);
}

/**
 * The read-side twin of `dropNonFiniteFilterConditions`, for server filters that were
 * persisted without it. Only the conditions go — the entry itself stays, so hydration
 * keeps the mode fork in `useGridRows` intact.
 */
export function dropPersistedNonFiniteFilterConditions(filters: ServerGridFilter[]): ServerGridFilter[];
export function dropPersistedNonFiniteFilterConditions(
  filters: ServerGridFilter[] | undefined
): ServerGridFilter[] | undefined;
export function dropPersistedNonFiniteFilterConditions(
  filters: ServerGridFilter[] | undefined
): ServerGridFilter[] | undefined {
  return dropFilterConditions(filters, hasPersistedNonFiniteValue);
}
